import React, { useEffect, useState } from 'react';
import { Drawer, Button, Checkbox, Input, Upload, Select, Typography, Space, Tag, Empty, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';

const { Text } = Typography;

const normFields = (fields = []) => fields.map((f) => (typeof f === 'string' ? { name: f } : f));

/**
 * 渠道配置抽屉：左侧渠道/平台列表 + 右侧当前渠道字段
 * - selectable=true：左侧可勾选（上架平台），勾选后才纳入
 * - 每个字段可填写或上传
 * - syncConfig：{ enabled, synced(Set), onSync(key, name) } —— 仅 syncable 渠道展示「同步到企业商户号」
 * config = { selected: [key...], data: { [key]: { [field]: value } } }
 */
const ChannelsDrawer = ({ open, item, value, isService, initialKey, syncConfig, onClose, onSave }) => {
    const channels = item?.channels || [];
    const selectable = !!item?.selectable;
    const [selected, setSelected] = useState([]);
    const [data, setData] = useState({});
    const [activeKey, setActiveKey] = useState(null);

    useEffect(() => {
        if (open) {
            const cfg = value?.config || {};
            const chs = item?.channels || [];
            const sel = selectable ? (cfg.selected || []) : chs.map((c) => c.key);
            setSelected(sel);
            setData(cfg.data || {});
            setActiveKey(initialKey || sel[0] || chs[0]?.key || null);
        }
    }, [open, value, initialKey, selectable, item]);

    const configured = (key) => Object.values(data[key] || {}).some((v) => v && String(v).trim());

    const toggle = (key, checked) => {
        setSelected((prev) => (checked ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)));
        if (checked) setActiveKey(key);
    };

    const setField = (key, name, val) => setData((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [name]: val } }));

    const handleSave = () => {
        const names = selected.map((k) => channels.find((c) => c.key === k)?.name).filter(Boolean);
        const hasField = Object.values(data).some((d) => Object.values(d || {}).some((v) => v && String(v).trim()));
        const summary = selectable
            ? (names.length ? `已选：${names.join('、')}` : '')
            : `已配置 ${channels.filter((c) => configured(c.key)).length}/${channels.length}`;
        const status = hasField || (selectable && selected.length > 0) ? 'draft' : 'pending';
        onSave(status, summary, { selected, data });
        message.success('已保存');
    };

    const activeChannel = channels.find((c) => c.key === activeKey);

    return (
        <Drawer
            title={item?.name || ''}
            open={open}
            width={760}
            onClose={onClose}
            footer={<div style={{ textAlign: 'right' }}><Space><Button onClick={onClose}>取消</Button><Button type="primary" onClick={handleSave}>{isService ? '代填保存' : '保存'}</Button></Space></div>}
        >
            <div style={{ display: 'flex', gap: 16, height: '100%' }}>
                <div style={{ width: 220, borderRight: '1px solid #f0f0f0', paddingRight: 12 }}>
                    {selectable && <div style={{ marginBottom: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>{item?.selectHint || '勾选要启用的渠道'}</Text></div>}
                    {channels.map((c) => (
                        <div
                            key={c.key}
                            onClick={() => setActiveKey(c.key)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px', borderRadius: 6,
                                cursor: 'pointer', background: activeKey === c.key ? '#e6f4ff' : 'transparent',
                            }}
                        >
                            {selectable && (
                                <Checkbox checked={selected.includes(c.key)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => toggle(c.key, e.target.checked)} />
                            )}
                            <Text style={{ flex: 1, fontSize: 13 }}>{c.name}</Text>
                            <Tag color={configured(c.key) ? 'success' : 'default'} style={{ border: 'none', marginInlineEnd: 0, fontSize: 11 }}>
                                {configured(c.key) ? '已配置' : '未配置'}
                            </Tag>
                        </div>
                    ))}
                </div>
                <div style={{ flex: 1, overflow: 'auto', paddingRight: 8 }}>
                    {activeChannel ? (
                        <>
                            <div style={{ marginBottom: 12 }}>
                                <Text strong>{activeChannel.name}</Text>
                                {activeChannel.push === false && <Tag style={{ marginLeft: 8 }}>无推送</Tag>}
                            </div>
                            {normFields(activeChannel.fields).map((f) => (
                                <div key={f.name} style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                    <Text style={{ width: 180, fontSize: 13 }}>{f.name}</Text>
                                    {f.type === 'select' ? (
                                        <Select
                                            style={{ flex: 1 }} placeholder="请选择" allowClear
                                            value={data[activeKey]?.[f.name]}
                                            options={(f.options || []).map((o) => ({ label: o, value: o }))}
                                            onChange={(v) => setField(activeKey, f.name, v)}
                                        />
                                    ) : f.type === 'file' ? (
                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Upload showUploadList={false} maxCount={1}
                                                beforeUpload={(file) => { setField(activeKey, f.name, file.name); return false; }}>
                                                <Button icon={<UploadOutlined />}>选择文件</Button>
                                            </Upload>
                                            {data[activeKey]?.[f.name] && <Text type="secondary" style={{ fontSize: 12 }}>{data[activeKey][f.name]}</Text>}
                                        </div>
                                    ) : (
                                        <Input
                                            style={{ flex: 1 }}
                                            placeholder={f.placeholder || '请输入'} allowClear
                                            value={data[activeKey]?.[f.name] || ''}
                                            onChange={(e) => setField(activeKey, f.name, e.target.value)}
                                        />
                                    )}
                                </div>
                            ))}

                            {item.syncable && syncConfig?.enabled && (
                                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                                    {syncConfig.synced?.has(activeKey) ? (
                                        <Space size={8}>
                                            <Tag color="success" style={{ border: 'none' }}>已同步</Tag>
                                            <Button type="link" size="small" style={{ padding: 0 }}
                                                onClick={() => syncConfig.onSync?.(activeKey, activeChannel.name, true)}>更新企业商户号</Button>
                                        </Space>
                                    ) : (
                                        <Space size={8}>
                                            <Button type="primary" size="small"
                                                onClick={() => syncConfig.onSync?.(activeKey, activeChannel.name, false)}>同步到企业商户号</Button>
                                            <Text type="secondary" style={{ fontSize: 12 }}>将在《企业商户号》新增该渠道记录</Text>
                                        </Space>
                                    )}
                                </div>
                            )}
                        </>
                    ) : <Empty description="请选择左侧渠道" />}
                </div>
            </div>
        </Drawer>
    );
};

export default ChannelsDrawer;
